  
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, timeout } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Capteur {
  id: number;
  type_capteur?: string | null;
  numero_serie: string;
  date_assemblage: string;
  numero_nomenclature: number;
  code_mac_carte_electronique: string;
  date_emballage: string;
  cartouche_a: number | null;
  cartouche_b: number | null;
  statut_capteur_id?: number | null;
  code_bon_retour: string;
  code_bon_retour_cartouches: string;
  date_envoi?: string | null;
  date_expedition?: string | null;
  reference_envoi: string;
  date_retour: string;
  nombre_heures_fonctionnement: string | null;
  nombre_litres_aspires: number;
  presence_cartouche: boolean;
  test_moteur?: boolean;
  commentaire: string;
  ligne_commande_id?: number | null;
}

export interface CapteurPayload {
  type_capteur?: string;
  numero_serie: string;
  date_assemblage?: string;
  numero_nomenclature: number;
  code_mac_carte_electronique?: string;
  date_emballage?: string;
  cartouche_a?: number | null;
  cartouche_b?: number | null;
  statut_capteur_id?: number | null;
  code_bon_retour?: string;
  code_bon_retour_cartouches?: string;
  date_envoi?: string;
  date_expedition?: string;
  reference_envoi?: string;
  date_retour?: string;
  nombre_heures_fonctionnement?: string;
  nombre_litres_aspires?: number;
  presence_cartouche?: boolean;
  test_moteur?: boolean;
  commentaire?: string;
  ligne_commande_id?: number | null;
}

export interface CapteurLivraisonItem {
  id: number;
  numero_serie: string;
  reference_envoi: string | null;
  cartouche_a: number | null;
  cartouche_b: number | null;
  cartouche_a_nom: string | null;
  cartouche_b_nom: string | null;
  statut_livraison_envoi: number | null;
  statut_livraison_envoi_nom: string | null;
}

export interface NextNumeroSerieResponse {
  prefix: string;
  numero_serie: string;
}

@Injectable({ providedIn: 'root' })
export class CapteursService {
  private readonly baseUrl = `${environment.apiBaseUrl}/api/capteurs`;
  private readonly requestTimeoutMs = 30000;

  constructor(private readonly http: HttpClient) {}

  list(): Observable<Capteur[]> {
    return this.http
      .get<{ items?: Capteur[] } | Capteur[]>(this.baseUrl)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((response) => {
          if (Array.isArray(response)) {
            return response;
          }
          return Array.isArray(response?.items) ? response.items : [];
        })
      );
  }

  getById(id: number): Observable<Capteur> {
    return this.http
      .get<Capteur>(`${this.baseUrl}/${id}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  create(payload: CapteurPayload): Observable<Capteur> {
    return this.http
      .post<Capteur>(this.baseUrl, payload)
      .pipe(timeout(this.requestTimeoutMs));
  }

  update(id: number, payload: CapteurPayload): Observable<Capteur> {
    return this.http
      .put<Capteur>(`${this.baseUrl}/${id}`, payload)
      .pipe(timeout(this.requestTimeoutMs));
  }

  delete(id: number): Observable<{ deleted: boolean; id: number }> {
    return this.http
      .delete<{ deleted: boolean; id: number }>(`${this.baseUrl}/${id}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  getByLigneCommande(ligne_commande_id: number): Observable<Capteur> {
    return this.http
      .get<Capteur>(`${this.baseUrl}/by-ligne-commande/${ligne_commande_id}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  getNextNumeroSerie(prefix: string): Observable<NextNumeroSerieResponse> {
    return this.http
      .get<NextNumeroSerieResponse>(`${this.baseUrl}/next-numero-serie?prefix=${encodeURIComponent(prefix)}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  listEnCoursDeLivraison(): Observable<CapteurLivraisonItem[]> {
    return this.http
      .get<{ items?: CapteurLivraisonItem[] } | CapteurLivraisonItem[]>(`${this.baseUrl}/livraisons/en-cours`)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((response) => {
          if (Array.isArray(response)) {
            return response;
          }
          return Array.isArray(response?.items) ? response.items : [];
        })
      );
  }

  listRetardsCapteurs(): Observable<CapteurLivraisonItem[]> {
    return this.http
      .get<{ items?: CapteurLivraisonItem[] } | CapteurLivraisonItem[]>(`${this.baseUrl}/retards/capteurs`)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((response) => {
          if (Array.isArray(response)) {
            return response;
          }
          return Array.isArray(response?.items) ? response.items : [];
        })
      );
  }
}
